import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface AddClassModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (classData: ClassFormData) => void;
}

export interface ClassFormData {
  name: string;
  code: string;
  term: string;
  description: string;
  syllabusLink: string;
  slidesLink: string;
  classType: string;
  assignmentTypes: string[];
  meetingDays: string[];
  meetingTime: string;
  location: string;
  credits: number;
}

const classTypes = [
  'Lecture',
  'Seminar',
  'Workshop',
  'Lab',
  'Discussion Section',
  'Studio',
  'Practicum',
  'Independent Study',
  'Field Work',
  'Online Course'
];

const assignmentTypes = [
  'Research Papers',
  'Problem Sets',
  'Case Studies',
  'Group Projects',
  'Presentations',
  'Lab Reports',
  'Essays',
  'Quizzes',
  'Exams',
  'Portfolios',
  'Reflective Journals',
  'Peer Reviews',
  'Creative Projects',
  'Field Reports'
];

const daysOfWeek = [
  'Monday',
  'Tuesday', 
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
];

const AddClassModal: React.FC<AddClassModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState<ClassFormData>({
    name: '',
    code: '',
    term: '',
    description: '',
    syllabusLink: '',
    slidesLink: '',
    classType: '',
    assignmentTypes: [],
    meetingDays: [],
    meetingTime: '',
    location: '',
    credits: 3
  });

  const [errors, setErrors] = useState<Partial<ClassFormData>>({});

  const handleInputChange = (field: keyof ClassFormData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleArrayToggle = (field: 'assignmentTypes' | 'meetingDays', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ClassFormData> = {};

    if (!formData.name.trim()) newErrors.name = 'Course name is required';
    if (!formData.code.trim()) newErrors.code = 'Course code is required';
    if (!formData.term.trim()) newErrors.term = 'Term is required';
    if (!formData.classType) newErrors.classType = 'Class type is required';
    if (formData.assignmentTypes.length === 0) {
      newErrors.assignmentTypes = ['At least one assignment type is required'] as any;
    }
    if (formData.meetingDays.length === 0) {
      newErrors.meetingDays = ['At least one meeting day is required'] as any;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
      // Reset form
      setFormData({
        name: '',
        code: '',
        term: '',
        description: '',
        syllabusLink: '',
        slidesLink: '',
        classType: '',
        assignmentTypes: [],
        meetingDays: [],
        meetingTime: '',
        location: '',
        credits: 3
      });
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">Add New Class</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Learning Design and Technology"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Code *
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                  errors.code ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., EDU401"
              />
              {errors.code && <p className="text-red-500 text-sm mt-1">{errors.code}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Term *
              </label>
              <input
                type="text"
                value={formData.term}
                onChange={(e) => handleInputChange('term', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                  errors.term ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Spring 2025"
              />
              {errors.term && <p className="text-red-500 text-sm mt-1">{errors.term}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Credits
              </label>
              <input
                type="number"
                min="1"
                max="12"
                value={formData.credits}
                onChange={(e) => handleInputChange('credits', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          {/* Course Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Course Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="Brief description of the course objectives and content..."
            />
          </div>

          {/* Links */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Syllabus Link
              </label>
              <input
                type="url"
                value={formData.syllabusLink}
                onChange={(e) => handleInputChange('syllabusLink', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="https://..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Slides Link
              </label>
              <input
                type="url"
                value={formData.slidesLink}
                onChange={(e) => handleInputChange('slidesLink', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="https://..."
              />
            </div>
          </div>

          {/* Class Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Class Type *
            </label>
            <select
              value={formData.classType}
              onChange={(e) => handleInputChange('classType', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                errors.classType ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select class type...</option>
              {classTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            {errors.classType && <p className="text-red-500 text-sm mt-1">{errors.classType}</p>}
          </div>

          {/* Assignment Types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assignment Types * (Select all that apply)
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {assignmentTypes.map(type => (
                <label key={type} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.assignmentTypes.includes(type)}
                    onChange={() => handleArrayToggle('assignmentTypes', type)}
                    className="rounded text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">{type}</span>
                </label>
              ))}
            </div>
            {errors.assignmentTypes && <p className="text-red-500 text-sm mt-1">{errors.assignmentTypes}</p>}
          </div>

          {/* Meeting Schedule */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meeting Days * (Select all that apply)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {daysOfWeek.map(day => (
                  <label key={day} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.meetingDays.includes(day)}
                      onChange={() => handleArrayToggle('meetingDays', day)}
                      className="rounded text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm text-gray-700">{day}</span>
                  </label>
                ))}
              </div>
              {errors.meetingDays && <p className="text-red-500 text-sm mt-1">{errors.meetingDays}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meeting Time
              </label>
              <input
                type="text"
                value={formData.meetingTime}
                onChange={(e) => handleInputChange('meetingTime', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="e.g., 2:00 PM - 3:30 PM"
              />
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="e.g., Room 204, Education Building"
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
            >
              Add Class
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddClassModal;